/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */

import { Injectable } from '@angular/core';
import { UndoRedoService } from './undo-redo.service';
import { EditorDialogService } from './editor-dialog.service';
import { ErrorService } from '../../../common/error.service';
import { ComponentInjectorService } from '../../../common/component-injector.service';
import { UploadAssetService } from '../../../common/upload-asset.service';
import { TranslationService } from '../../../common/translation.service';
import { OnedriveAssetLibraryService } from '../../../common/onedrive-assetlibrary.service';
import { BulbCKEStateService } from './bulb-cke-state.service';
import { ImageCropperDialogService } from '../../../app/image-cropper/image-cropper-dialog.service';

/**
 * Service which will have all services as a field to use into cke.
 */
@Injectable({
  providedIn: 'root',
})
export class CKEditorService {
  /**
   * Stores the information of the element over which the element was dropped.
   */
  static dropTargetElement: Element;

  /**
   *
   * @param $translate Our Angular Translation service.
   * @param bulbCKEStateService Service which is having all subject related to editor.
   * @param componentInjectorService Injector service for dynamically load component.
   * @param editorDialogService
   * @param errorService Our custom error service.
   * @param onedriveAssetLibraryService Service for one drive api.
   * @param undoRedoService Service which handle api communication for page editor.
   * @param uploadAssetService Service to handle uploads.
   */
  constructor(
    private $translate: TranslationService,
    private bulbCKEStateService: BulbCKEStateService,
    private componentInjectorService : ComponentInjectorService,
    private editorDialogService : EditorDialogService,
    private errorService: ErrorService,
    private onedriveAssetLibraryService : OnedriveAssetLibraryService,
    private undoRedoService : UndoRedoService,
    private uploadAssetService : UploadAssetService,
    private imageCropperDialogService : ImageCropperDialogService,
  ) {

  }

  /**
   * Get component inject service to inject angular component into cke.
   */
  get componentInjector(): ComponentInjectorService {
    return this.componentInjectorService;
  }

  /**
   * Get editor dialog service to show modal for CKE plugin.
   */
  get dialog(): EditorDialogService {
    return this.editorDialogService;
  }

  /**
   * Get editor dialog service to show modal for CKE plugin.
   */
  get imageDialog(): ImageCropperDialogService {
    return this.imageCropperDialogService;
  }

  /**
   * Get error service to throw exception and log error.
   */
  get error(): ErrorService {
    return this.errorService;
  }

  /**
   * Get service to use onedrive functionality.
   */
  get oneDrive(): any {
    return this.onedriveAssetLibraryService;
  }

  /**
   * Get translation service to get angularjs translations into cke.
   */
  get translate(): any {
    return this.$translate;
  }

  /**
   * Get service to handle undo redo for cke model.
   */
  get undoRedo(): UndoRedoService {
    return this.undoRedoService;
  }

  /**
   * Get upload service to upload media from cke page.
   */
  get uploadAsset(): UploadAssetService {
    return this.uploadAssetService;
  }

  /**
   * Get CKE state service.
   */
  get ckeStateService(): BulbCKEStateService {
    return this.bulbCKEStateService;
  }

  /**
   * Add paragraph at the dropped location to avoid replacing the original content.
   * @param editor Current editor instance.
   * @param data Data from the event.
   */
  addParagraphBeforeDroppedBlock(editor, data) {
    if (data.method === 'drop' && CKEditorService.dropTargetElement.nodeName !== 'P'
      && !CKEditorService.dropTargetElement.classList.contains('ck-editor__editable')) {
      const ranges = data.targetRanges.map((viewRange) => editor.editing.mapper.toModelRange(viewRange));
      editor.execute('insertParagraph', {
        position: editor.model.createPositionAt(ranges[0].start, 'before'),
      });
      const editingView = editor.editing.view;
      editingView.focus();
      editor.editing.view.scrollToTheSelection();
      CKEditorService.dropTargetElement = null;
    }
  }

  /**
   * Register events for adding relevant margin to list elements on user actions.
   * @param editor CK editor instance.
   */
  registerEventsForCkeLists(editor) {
    // Event registered on document to handle events on ck toolbar.
    document.addEventListener('mouseup', this.addMarginToListForCKEditor);

    editor.editing.view.document.on('keyup', (event, data) => {
      this.addMarginToListForCKEditor();
    });

    editor.editing.view.document.on('drop', (event, data) => {
      this.addMarginToListForCKEditor();
    });
  }

  /**
   * Add margin to list elements to create indentation effect.
   */
  addMarginToListForCKEditor() {
    setTimeout(() => {
      const pageContentDiv = document.querySelector('div.ck.ck-editor__main');
      if (pageContentDiv) {
        Array.from(['ul', 'ol']).forEach((listEle) => {
          Array.from(pageContentDiv.getElementsByTagName(listEle)).forEach((ele) => {
            const htmlElement: HTMLElement = ele as HTMLElement;
            if (!ele.closest('table') && !ele.parentElement.closest('ul') && !ele.parentElement.closest('ol')) {
              const listStyleType = htmlElement.style.listStyleType ?? '';
              ele.classList.add('list-margin');
              // 2 rem indentation between normal text and list elements.
              ele.setAttribute('style', `margin-left:${htmlElement.offsetLeft + 32}px;`);
              htmlElement.style.listStyleType = listStyleType;
              ele.classList.remove('list-margin');
            }
          });
        });
      }
    });
  }
}
